import { createColumnHelper } from '@tanstack/react-table';
import { Controller, useFormContext } from 'react-hook-form';
import { ProductSection } from '../ProductSection/ProductSection';
import { CheckboxOption } from '../CheckboxOption/CheckboxOption';
import { Button, ButtonTypes, Input } from '@common/components';
import { ErrorMessage } from '../ErrorMessage';
import { IAmpId } from '@common/features/product/types';
import DatapharmTable from '@components/DatapharmTable/DatapharmTable';
import { IProductForm, ProductFormPaths } from '@common/features/product/components/ProductContent/hooks/useProductForm';
import { StyledSVG } from '@common/styles';
import * as Styled from './styles';
import * as CommonStyles from '../styles';

interface IAmpIdProps {
  elementId: string;
  ampIds: IAmpId[];
  ampIdToAdd: string;
  isNoAmpId: boolean;
  readonly?: boolean;
  onAmpIdAdd: () => void;
  onAmpIdRemove: (index: number) => void;
  onNoAmpIdChange: () => void;
}

export const AmpIds = ({ elementId, ampIds, isNoAmpId, onAmpIdAdd, onAmpIdRemove, onNoAmpIdChange, readonly }: IAmpIdProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<IProductForm>();

  const isDataEmpty = !ampIds.length && !isNoAmpId;

  const errorMessage = errors?.productDetails?.ampIdForm?.ampIdInput?.message;

  const columnHelper = createColumnHelper<IAmpId>();

  const columns = [
    columnHelper.accessor('id', {
      header: 'Code',
    }),
    ...(readonly
      ? []
      : [
          columnHelper.display({
            header: 'Action',
            cell: (item) => {
              return (
                <CommonStyles.RemoveButton onClick={() => onAmpIdRemove(parseInt(item.cell.id))}>
                  <StyledSVG src={`/icons/trash_bin.svg`} />
                </CommonStyles.RemoveButton>
              );
            },
          }),
        ]),
  ];

  return (
    <ProductSection
      elementId={elementId}
      heading="AMP ids"
      subtext="The AMP id is the SNOMED code for the Actual Medicinal Product as stated on the NHS's Dictionary of Medicines and devices."
      withError={!readonly && isDataEmpty}
    >
      <Styled.AmpIdWrap>
        <CheckboxOption
          item={{ id: 'noAmpId', text: 'No AMP id is available' }}
          name="noAmpId"
          isChecked={isNoAmpId}
          onChange={onNoAmpIdChange}
          disabled={readonly}
        />
        {!readonly && (
          <>
            <CommonStyles.InputButtonWrapper>
              <Controller
                control={control}
                name={ProductFormPaths.ampIdInput}
                render={({ field: { value = '', onChange } }) => (
                  <Input
                    inputType={'number'}
                    min="0"
                    label="Enter AMP id"
                    name="Amp Id"
                    disabled={isNoAmpId}
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
              <Button
                disabled={isNoAmpId}
                text="Add"
                type={ButtonTypes.PRIMARY_BORDER}
                onClick={onAmpIdAdd}
              />
            </CommonStyles.InputButtonWrapper>
            {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
          </>
        )}
        {!isNoAmpId && ampIds.length > 0 && <DatapharmTable columns={columns} documents={ampIds} />}
      </Styled.AmpIdWrap>
    </ProductSection>
  );
};
